/**
 * Bike Page Header
 * ================
 * Shared header component for all motorcycle sub-pages.
 * Shows bike info and navigation tabs.
 */

import { Link, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  ArrowLeft,
  Info,
  History,
  Wrench,
  Hammer,
  Package,
  Search,
  Bike,
} from 'lucide-react'
import type { Motorcycle } from '@/types'
import { BIKE_STATUS_COLORS, BIKE_STATUS_LABELS } from '@/types'

interface BikePageHeaderProps {
  bike: Motorcycle | null | undefined
  isLoading?: boolean
}

const tabs = [
  { label: 'Information', path: 'information', icon: Info },
  { label: 'History', path: 'history', icon: History },
  { label: 'Service', path: 'service', icon: Wrench },
  { label: 'Restoration/Build', path: 'build', icon: Hammer },
  { label: 'Parts on Stock', path: 'parts', icon: Package },
  { label: 'Parts Search', path: 'search', icon: Search },
]

export default function BikePageHeader({ bike, isLoading }: BikePageHeaderProps) {
  const location = useLocation()
  const currentPath = location.pathname.split('/').pop()

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
        </div>
      </div>
    )
  }

  if (!bike) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <p className="text-red-600">Motorcycle not found</p>
        <Link
          to="/motorcycles"
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mt-4"
        >
          <ArrowLeft size={16} />
          Back to Motorcycles
        </Link>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
      {/* Back link and bike info */}
      <div className="p-6 border-b dark:border-gray-700">
        <Link
          to="/motorcycles"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 text-sm mb-4"
        >
          <ArrowLeft size={16} />
          Back to Motorcycles
        </Link>

        <div className="flex items-start gap-4">
          {/* Bike image */}
          <div className="w-24 h-24 rounded-lg bg-gray-100 dark:bg-gray-700 flex-shrink-0 overflow-hidden">
            {bike.image_url ? (
              <img
                src={bike.image_url}
                alt={bike.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Bike size={32} className="text-gray-300 dark:text-gray-600" />
              </div>
            )}
          </div>

          {/* Bike details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">
                {bike.name}
              </h1>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  BIKE_STATUS_COLORS[bike.status] || BIKE_STATUS_COLORS.running
                }`}
              >
                {BIKE_STATUS_LABELS[bike.status] || bike.status}
              </span>
            </div>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              {bike.year} {bike.make} {bike.model}
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {bike.motorcycle_id}
            </p>
          </div>

          {/* Edit button */}
          <Link
            to={`/motorcycles/${bike.motorcycle_id}/edit`}
            className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 border border-primary-600 dark:border-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
          >
            Edit
          </Link>
        </div>
      </div>

      {/* Navigation tabs */}
      <div className="px-6 overflow-x-auto">
        <nav className="flex gap-1 -mb-px">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = currentPath === tab.path

            return (
              <Link
                key={tab.path}
                to={`/motorcycles/${bike.motorcycle_id}/${tab.path}`}
                className={clsx(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  isActive
                    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                )}
              >
                <Icon size={16} />
                {tab.label}
              </Link>
            )
          })}
        </nav>
      </div>
    </div>
  )
}
